/**
 * Perspective Types and Configuration (Issue #1799, replaces #1767)
 *
 * Field = abstract domain the user works in (profile setting, stable).
 * Perspective = concrete role the AI speaks as (per-query, changeable).
 *
 * Each field has a default perspective and recommended sub-perspectives.
 * All perspectives use the same backend template — no special cases.
 */

/** Explicit opt-out perspective value (no specific role, never auto-resolved). */
export const DEFAULT_PERSPECTIVE = 'general';

/**
 * Auto perspective sentinel — the backend resolves it from the user's profile
 * field (`users.profession`). This is the default lens: adaptation stays on,
 * but the UI must display the resolved role so the pick is never silent.
 */
export const AUTO_PERSPECTIVE = 'auto';

/**
 * Mapping from user field (DB `users.profession` key) to default perspective
 * (Slovak person/role label). Used to pre-select perspective when user has a field set.
 */
export const FIELD_DEFAULT_PERSPECTIVES: Record<string, string> = {
  citizen: 'Občan',
  entrepreneur: 'Podnikateľ',
  lawyer: 'Advokát',
  accountant: 'Účtovník',
  tax_advisor: 'Daňový poradca',
  government_official: 'Štátny úradník',
  municipality_representative: 'Pracovník samosprávy',
  student: 'Akademik',
  healthcare_professional: 'Zdravotník',
  construction_developer: 'Stavebník',
  security_force_member: 'Príslušník bezpečnostných zložiek',
  non_profit_organization: 'Pracovník v neziskovom sektore',
  judge: 'Sudca',
  notary: 'Notár',
  judicial_executor: 'Exekútor',
};

/**
 * Curated perspective suggestions shown in "All perspectives" section.
 * These are common legal analysis roles useful across all fields.
 */
export const FEATURED_PERSPECTIVES = [
  'Advokát žalobcu',
  'Advokát žalovaného',
  'Sudca',
  'Akademik',
  'Občan',
] as const;

/**
 * Field-to-recommended-perspectives mapping.
 * Keys are user field values from the profile settings (DB `users.profession`).
 * Values are arrays of perspective display labels (Slovak) shown in "Recommended" section.
 * These are domain-specific sub-role perspectives for each field.
 */
export const PROFILE_RECOMMENDED_PERSPECTIVES: Record<string, string[]> = {
  citizen: [
    'Rodič',
    'Zamestnanec',
    'Nájomník',
    'Spotrebiteľ',
    'Pacient',
    'Poškodený',
  ],
  entrepreneur: [
    'Konateľ',
    'Živnostník',
    'Zamestnávateľ',
    'Dodávateľ',
    'Odberateľ',
  ],
  lawyer: [
    'Advokát žalobcu',
    'Advokát žalovaného',
    'Obhajca',
    'Koncipient',
    'Mediátor',
    'Správca konkurznej podstaty',
  ],
  accountant: [
    'Hlavný účtovník',
    'Audítor',
    'Daňový kontrolór',
    'Mzdový účtovník',
  ],
  tax_advisor: ['Daňový poradca FO', 'Daňový poradca PO', 'Colný poradca'],
  government_official: [
    'Štátny zamestnanec',
    'Úradník stavebného úradu',
    'Inšpektor',
    'Správca dane',
  ],
  municipality_representative: [
    'Starosta/Starostka',
    'Prednosta OÚ',
    'Hlavný kontrolór',
    'Poslanec zastupiteľstva',
    'Matrikár/ka',
  ],
  student: ['Študent práva', 'Doktorand', 'Výskumník'],
  healthcare_professional: [
    'Lekár',
    'Farmaceut',
    'Zdravotná sestra',
    'Poskytovateľ ZS',
    'Revízny lekár',
  ],
  construction_developer: [
    'Stavebník',
    'Projektant',
    'Stavbyvedúci',
    'Investor',
    'Správca budovy',
  ],
  security_force_member: [
    'Policajt',
    'Hasič',
    'Príslušník ZVJS',
    'Colník',
    'Vojak',
  ],
  non_profit_organization: [
    'Štatutár OZ',
    'Správca nadácie',
    'Dobrovoľník',
    'Fundraiser',
  ],
  judge: [
    'Sudca OS',
    'Sudca KS',
    'Sudca NS SR',
    'Sudca ÚS SR',
    'Sudca správneho súdu',
  ],
  notary: ['Notár', 'Notársky kandidát', 'Súdny komisár'],
  judicial_executor: [
    'Exekútor',
    'Exekútorský koncipient',
    'Vyšší súdny úradník',
  ],
};

/** Default recommended perspectives for unknown/other fields. */
export const DEFAULT_RECOMMENDED_PERSPECTIVES = ['Občan', 'Sudca', 'Akademik'];

/** Maximum number of MRU (most recently used) perspectives to track. */
export const MAX_MRU_PERSPECTIVES = 3;
